import { CSSProperties, FormEvent, FunctionComponent, useState } from 'react';
import { WebEntity } from '../../entities/types';
import { BasicEntity } from '../../types';
import { capitalize } from '../../utils/utils';
import Modal from '@mui/material/Modal';
import GenericIcons from './GenericIcons';
import GenericForm from './GenericForm';

const ModalButton: FunctionComponent<{ buttonName: string; handleOpen: () => void }> = ({
  buttonName,
  handleOpen
}) => <button onClick={handleOpen}>{buttonName}</button>;

const renderModalButton = (
  alternativeButton: ((handleOpen: () => void) => JSX.Element) | undefined,
  handleOpen: () => void,
  name: string
) =>
  alternativeButton ? (
    alternativeButton(handleOpen)
  ) : (
    <ModalButton handleOpen={handleOpen} buttonName={name} />
  );

type Props<T> = {
  webEntity: WebEntity<T>;
  isAuthenticated: boolean;
  isAdmin: boolean;
  actionFn: (formFields: any) => Promise<any>;
  alternativeButton?: (toggle: () => void) => React.JSX.Element;
  entityToEdit?: BasicEntity & {
    [key: string]: any;
  };
  title?: string;
  centered?: boolean;
  closeOnClickOut?: boolean;
  actionName?: string;
  buttonName?: string;
};

const GenericModalSidebar = <T,>({
  webEntity,
  isAuthenticated,
  isAdmin,
  actionFn,
  alternativeButton,
  entityToEdit,
  title = '',
  actionName = '',
  buttonName = ''
}: Props<T>) => {
  const [open, setOpen] = useState<boolean>(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const onSubmit = async (e: FormEvent, formFields: T) => {
    e.preventDefault();

    // Add Entity
    const res = await actionFn(formFields);
    if (res) {
      handleClose();
    }
  };

  const { name, fields, editableFields } = webEntity;

  if (entityToEdit && !editableFields) {
    throw new Error(`Entity [${webEntity.name}] does not provide a list of editable fields`);
  } else if (!fields)
    throw new Error(`Entity [${webEntity.name}] does not provide a list of fields`);

  return (
    <>
      {isAuthenticated ? (
        renderModalButton(
          alternativeButton,
          handleOpen,
          buttonName || `Agregar ${capitalize(name)}`
        )
      ) : (
        <h4 className='mb-3 ml4'>{`Identifícate para administrar ${name}`}</h4>
      )}
      <Modal
        open={open}
        onClose={handleClose}
        sx={{ zIndex: 999, '& .MuiBackdrop-root': { backgroundColor: 'rgba(11, 14, 16, 0.55)' } }}>
        <div style={Styles.modal}>
          <div style={Styles.closeContainer}>
            <button
              type='button'
              className='vdr-gen-icon-btn'
              aria-label='Cerrar'
              onClick={() => handleClose()}>
              <GenericIcons icon='closeIcon' imageStyle={{ height: '100%' }} />
            </button>
          </div>
          <h2 style={Styles.title}>{title || buttonName || `Agregar ${name}`}</h2>
          <GenericForm
            fields={entityToEdit && editableFields ? editableFields : fields ? fields : []}
            onSubmit={onSubmit}
            actionName={actionName}
            isAdmin={isAdmin}
            entityToEdit={entityToEdit}
            callbackCancelButton={handleClose}
          />
        </div>
      </Modal>
    </>
  );
};

// Panel lateral blanco con franja cian arriba y título Archivo 800 (marca).
const Styles: { [key: string]: CSSProperties } = {
  modal: {
    position: 'absolute' as 'absolute',
    backgroundColor: 'var(--vdr-white)',
    top: '0',
    right: '0',
    width: 'clamp(360px, 30vw, 520px)',
    height: '100%',
    padding: '24px 32px 32px',
    overflowY: 'auto',
    borderTop: '4px solid var(--vdr-cyan)',
    borderLeft: '1px solid var(--vdr-line)',
    boxShadow: 'var(--vdr-shadow-overlay)',
    outline: 'none'
  },
  closeContainer: {
    width: '100%',
    height: '32px',
    display: 'flex',
    justifyContent: 'flex-end'
  },
  title: {
    fontFamily: 'var(--vdr-font-display)',
    fontSize: 'clamp(22px, 1.7vw, 28px)',
    fontWeight: 800,
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: '1.2',
    letterSpacing: '-0.01em',
    textAlign: 'left',
    color: 'var(--vdr-ink)',
    textTransform: 'none',
    margin: '10px 0 24px',
    paddingBottom: '14px',
    borderBottom: '1px solid var(--vdr-line)'
  }
};

export default GenericModalSidebar;
